import { type Scope, createContextScope } from '@radix-ui/react-context'
import { forwardRef } from 'react'
import { Button } from '~/components/button'
import { cn } from '~/utils/cn'
import {
  SearchBarButton,
  SearchBarClear,
  SearchBarContainer,
  SearchBarInput,
} from './search-bar.styles'
import type {
  SearchBarContext,
  SearchBarProps,
  SearchBarScopedProps,
} from './search-bar.types'

const DISPLAY_NAME = 'SearchBar'

const SearchBarRoot = forwardRef<HTMLInputElement, SearchBarProps>(
  (props: SearchBarScopedProps<SearchBarProps>, ref) => {
    const {
      onChange = () => {},
      handleSearch = () => {},
      handleClear = () => {},
      placeholder,
      datatestid = 'search-bar',
      value = '',
      size = 'md',
      __scopeSearchBar,
    } = props

    const btnSizeMapper = (value: SearchBarProps['size']) => {
      if (value === 'lg') return 'ml'
      return value
    }

    return (
      <SearchBarProvider {...props} scope={__scopeSearchBar}>
        <div
          className={cn(
            SearchBarContainer({
              size,
              hasFilled: value !== '',
            })
          )}
        >
          <input
            ref={ref}
            type="text"
            className={SearchBarInput({ size, hasFilled: value !== '' })}
            placeholder={placeholder}
            onChange={e => onChange(e)}
            value={value}
            data-testid={datatestid}
          />
          <Button.Root
            size={btnSizeMapper(size)}
            variant="neutral-tertiary"
            className={SearchBarClear({ size })}
            onClick={handleClear}
          >
            <Button.Icon symbol="rdicon-dismiss-circle-outline" />
          </Button.Root>
          <Button.Root
            size={btnSizeMapper(size)}
            variant="neutral-tertiary"
            className={SearchBarButton({ size })}
            onClick={handleSearch}
          >
            <Button.Icon symbol="rdicon-search" />
          </Button.Root>
        </div>
      </SearchBarProvider>
    )
  }
)

SearchBarRoot.displayName = DISPLAY_NAME

/*
----------------------------------------------------------------
Scope Definition
----------------------------------------------------------------
*/

const [createSearchBarContext] = createContextScope(DISPLAY_NAME)

export const [SearchBarProvider, useSearchBarContext]: readonly [
  ProviderType<SearchBarContext>,
  (consumerName: string, scope: Scope) => SearchBarContext,
] = createSearchBarContext<SearchBarContext>(DISPLAY_NAME)

/*
----------------------------------------------------------------
Composition Export
----------------------------------------------------------------
*/

export const SearchBar = {
  Root: SearchBarRoot,
}
